import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from 'expo-router';
import { useAccountLookup } from 'features/auth/resetPassword/hooks/useAccountLookup/useAccountLookup';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { emailSchema } from 'shared/components/vxl/input/schemas/email.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import KeyboardAvoidingContainer from 'shared/containers/KeyboardAvoidingContainer/KeyboardAvoidingContainer';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { z } from 'zod';

const findAccountSchema = z.object({
  email: emailSchema,
});

type FindAccountFormValues = z.infer<typeof findAccountSchema>;

const FindAccount = () => {
  const { t: translatedText } = useTranslation('auth', {
    keyPrefix: 'resetPassword.emailReview',
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FindAccountFormValues>({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
    resolver: zodResolver(findAccountSchema),
  });

  const { lookupAccount, loading, error } = useAccountLookup();

  const handleLookupAccount = ({ email }: FindAccountFormValues) => {
    void lookupAccount({
      emailAddress: email,
    });
  };

  const handleSubmitValidAccount = () => {
    void handleSubmit(handleLookupAccount)();
  };

  const handleErrorAnalytics = usePressableAnalytics({
    analyticsId: 'getErrorMessage',
    customAttributes: {
      error_type: translatedText('system'),
      error_description: error as string,
    },
  });

  useBackHandler(navigateBack);

  useEffect(() => {
    if (error !== '') {
      handleErrorAnalytics();
    }
    // Need below rule as I need to fire event on every message change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <KeyboardAvoidingContainer>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerShown: true,
          headerBackVisible: true,
          header: () => (
            <NavigationBar
              title={translatedText('title')}
              primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
              primaryOnPress={navigateBack}
              transparent={false}
            />
          ),
        }}
      />

      <KeyboardAvoidingContainer.Content>
        <TitleLgText testID="find-account-title" numberOfLines={0}>
          {translatedText('header')}
        </TitleLgText>

        <BodyBaseText numberOfLines={0}>
          {translatedText('description')}
        </BodyBaseText>
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <VXLTextInput
              labelText={translatedText('textField.email')}
              onChangeText={onChange}
              onBlur={onBlur}
              value={value}
              analyticsId="emailFieldClicked"
              autoFocus
              errorText={fieldState.error?.message}
              autoCapitalize="none"
              keyboardType="email-address"
              onSubmitEditing={handleSubmitValidAccount}
              testID="text-input-textField.email"
            />
          )}
        />
      </KeyboardAvoidingContainer.Content>

      <KeyboardAvoidingContainer.Actions>
        <Button
          label={translatedText('buttons.next')}
          onPress={handleSubmitValidAccount}
          analyticsId="nextClicked"
          size="LARGE"
          type="PRIMARY"
          width="FULL"
          isDisabled={!isValid}
          isLoading={loading}
        />
      </KeyboardAvoidingContainer.Actions>
    </KeyboardAvoidingContainer>
  );
};

export default FindAccount;
